import { useState, useRef, useEffect, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { 
  Header, 
  Breadcrumb, 
  BreadcrumbList, 
  BreadcrumbItem, 
  BreadcrumbLink, 
  BreadcrumbSeparator, 
  BreadcrumbPage, 
  Field,
  ContentContainer,
  Button,
  Alert,
  AlertTitle,
  AlertDescription,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  FileUpload,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  toast,
  Progress,
  Spinner
} from 'prosci-ui';
import { usePageContext } from '@/contexts/page-context';
import {
  Download,
  Trash2,
  CheckCircle,
  XCircle,
  AlertCircle
} from '@/lib/icons';
import { batchUploadService, type BatchUploadStatus } from '@/services/batch-upload-service';

export function BulkDeletePage() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { setShowPageHeader } = usePageContext();

  useEffect(() => {
    setShowPageHeader(false);
    return () => setShowPageHeader(true);
  }, [setShowPageHeader]);

  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedError, setSelectedError] = useState<string>('');
  const previousStatusesRef = useRef<Record<string, string>>({});

  // Poll for job status (more frequently to catch quick jobs)
  const { data: jobStatuses, refetch: refetchStatuses } = useQuery({
    queryKey: ['bulk-delete-status'],
    queryFn: () => batchUploadService.getJobStatus(),
    refetchInterval: 1000, // Poll every 1 second
  });

  // Filter to only delete jobs
  const deleteJobs = useMemo(
    () => jobStatuses?.data?.filter(job => job.job_type === 'delete') || [],
    [jobStatuses?.data]
  );

  // Monitor job status changes and show notifications
  useEffect(() => {
    if (!deleteJobs || deleteJobs.length === 0) return;

    deleteJobs.forEach((status) => {
      const previousStatus = previousStatusesRef.current[status.id];

      // Only show notification if status changed to completed or failed
      if (previousStatus && previousStatus !== status.status) {
        if (status.status === 'completed') {
          toast.success(`Bulk Delete Complete: Successfully deleted ${status.processed_count} of ${status.total_count} users.`);
        } else if (status.status === 'completed_with_errors') {
          toast.error(`Bulk Delete Completed with Errors: Deleted ${status.processed_count} of ${status.total_count} users. Some users could not be deleted.`);
        } else if (status.status === 'failed') {
          toast.error('Bulk Delete Failed: The bulk delete operation failed. Please try again.');
        }
      }
    });

    // Update previous statuses in ref (no re-render)
    deleteJobs.forEach((status) => {
      previousStatusesRef.current[status.id] = status.status;
    });
  }, [deleteJobs]);
  
  // Download template mutation
  const downloadTemplateMutation = useMutation({
    mutationFn: () => batchUploadService.downloadDeleteTemplate(),
    onError: () => {
      toast.error('Failed to download template. Please try again.');
    },
  });
  
  // Upload process
  const processDeleteMutation = useMutation({
    mutationFn: async (file: File) => {
      setIsUploading(true);
      setUploadProgress(0);
      
      try {
        // Get presigned URL
        setUploadProgress(10);
        const { upload_url, key } = await batchUploadService.getUploadUrl();
        
        // Upload to S3
        setUploadProgress(30);
        await batchUploadService.uploadToS3(file, upload_url);
        
        // Process delete
        setUploadProgress(70);
        await batchUploadService.processDelete(key);
        
        setUploadProgress(100);

        // Clear file and refetch statuses immediately and repeatedly for fast jobs
        setUploadFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

        // Refetch multiple times to catch quick completions
        refetchStatuses();
        setTimeout(() => refetchStatuses(), 500);
        setTimeout(() => refetchStatuses(), 1000);
        setTimeout(() => refetchStatuses(), 2000);
        
      } catch (error) {
        console.error('Delete process failed:', error);
        toast.error('Delete process failed. Please try again.');
      } finally {
        setIsUploading(false);
        setUploadProgress(0);
      }
    },
  });
  
  const handleFileSelect = (files: File[]) => {
    if (files.length > 0) {
      const file = files[0];
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        setUploadFile(file);
      } else {
        toast.error('Please select a CSV file');
        setUploadFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } else {
      setUploadFile(null);
    }
  };
  
  const handleUpload = () => {
    if (uploadFile) {
      setShowConfirmModal(true);
    }
  };

  const handleConfirmDelete = () => {
    if (uploadFile) {
      processDeleteMutation.mutate(uploadFile);
      setShowConfirmModal(false);
    }
  };
  
  const showErrors = (errors: string) => {
    setSelectedError(errors);
    setShowErrorModal(true);
  };
  
  const getStatusIcon = (status: BatchUploadStatus) => {
    switch (status.status) {
      case 'pending':
        return <AlertCircle className="h-5 w-5 text-gray-400" />;
      case 'in_progress':
        return <Spinner size="small" />;
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'completed_with_errors':
        return <AlertCircle className="h-5 w-5 text-yellow-600" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-600" />;
    }
  };
  
  const getStatusText = (status: BatchUploadStatus) => {
    switch (status.status) {
      case 'pending':
        return 'Pending';
      case 'in_progress':
        return `Processing... ${status.percentage_complete}%`;
      case 'completed':
        return 'Completed';
      case 'completed_with_errors':
        return 'Completed with errors';
      case 'failed':
        return 'Failed';
    }
  };

  return (
    <>
      <Header 
        title="Bulk Delete Users"
        size="small"
        breadcrumb={
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/users">Users</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Bulk Delete</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        }
      />
      <ContentContainer className="space-y-2">
        
        {/* Warning Message */}
        <Alert variant="destructive">
          <AlertCircle />
          <AlertTitle>Warning</AlertTitle>
          <AlertDescription>
            This action will permanently delete users from the system. This cannot be undone.
            Please ensure you have the correct list of users before proceeding.
          </AlertDescription>
        </Alert>
        
        {/* Download Template Section */}
        <Card size="medium">
          <CardHeader>
            <CardTitle>1. Download Template</CardTitle>
            <CardDescription>
              Download a CSV template with the correct format for bulk deletion.
              The template contains a single column for email addresses.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => downloadTemplateMutation.mutate()}
              disabled={downloadTemplateMutation.isPending}
            >
              {downloadTemplateMutation.isPending ? (
                <Spinner size="small" color="light" />
              ) : (
                <Download />
              )}
              Download Delete Template
            </Button>
          </CardContent>
        </Card>
        
        {/* Upload Section */}
        <Card size="medium">
          <CardHeader>
            <CardTitle>2. Upload Delete List</CardTitle>
            <CardDescription>
              Upload a CSV file with the list of users to delete.
              The file should contain a single column for email addresses.
            </CardDescription>
          </CardHeader>
          <CardContent>
          
          <div className="space-y-4">
            <Field>
              <FileUpload
                ref={fileInputRef}
                label="Upload CSV file"
                accept=".csv,text/csv"
                onFilesChange={handleFileSelect}
                buttonText="Choose File"
                showFileName={true}
              />
            </Field>
            
            {isUploading && (
              <Progress 
                value={uploadProgress} 
                max={100}
                size="sm"
              />
            )}
            
            <Button
              onClick={handleUpload}
              disabled={!uploadFile || isUploading}
              variant="destructive"
            >
              {isUploading ? (
                <Spinner size="small" color="light" />
              ) : (
                <Trash2 />
              )}
              Delete Users
            </Button>
          </div>
          </CardContent>
        </Card>
        
        {/* Recent Jobs Status */}
        {deleteJobs.length > 0 && (
          <Card size="medium">
            <CardHeader>
              <CardTitle>Recent Delete Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {deleteJobs.map((status) => (
                  <div key={status.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      {getStatusIcon(status)}
                      <div>
                        <p className="font-medium">{getStatusText(status)}</p>
                        <p className="text-sm text-muted-foreground">
                          Started {new Date(status.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {status.total_count && (
                        <span className="text-sm text-muted-foreground">
                          {status.processed_count} / {status.total_count} users deleted
                        </span>
                      )}
                      {status.has_errors && (
                        <Button
                          onClick={() => showErrors(status.errors || '')}
                          variant="outline"
                        >
                          View Errors
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Confirmation Modal */}
        <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm Bulk Delete</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete all users in this file? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                onClick={() => setShowConfirmModal(false)}
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmDelete}
                variant="destructive"
              >
                Delete Users
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Error Modal */}
        <Dialog open={showErrorModal} onOpenChange={setShowErrorModal}>
          <DialogContent className="max-w-2xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle>Delete Errors</DialogTitle>
            </DialogHeader>
            <div className="overflow-y-auto max-h-[60vh]">
              <pre className="text-sm whitespace-pre-wrap">{selectedError}</pre>
            </div>
            <DialogFooter>
              <Button
                onClick={() => setShowErrorModal(false)}
                variant="outline"
              >
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </ContentContainer>
    </>
  );
}